import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RouterProvider } from "react-router/dom";

import buildEnvironment from "@/buildEnv";
import { useCurrentUser } from "@hooks/useCurrentUser";
import AppSettings from "../../appsettings";
import {
  isDesktopView,
  isMobileView,
  isTabletView,
} from "../../screens/utils/getScreenSize";
import { AUTH_CONTROL_PATHS } from "../../services/auth/authRouteConfig";
import {
  getCurrentUser,
  setCurrentUserPictureUrl,
} from "../../services/auth/currentUser";
import { getUserManagerPromise } from "../../services/auth/authSessionRuntime";
import {
  AUTH_BOOTSTRAP_PHASES,
  bootstrapAuthenticatedSession,
} from "../../services/auth/sessionBootstrapOrchestrator";
import { getNitroUserData } from "../../services/nitroService";
import { getAnnouncements } from "../../services/notificationService";
import { getProfile } from "../../services/profileService";
import { printableDocumentEvents } from "../../utils/printableUtils.js";
import { createCookie } from "../utils/cookie";
import { AppShellContext } from "./AppShellContext";
import { useAuthTokenLifecycle } from "./useAuthTokenLifecycle";
import { useUserActivity } from "./useUserActivity";

const isProdBuild = buildEnvironment.toLowerCase() === "prod";

// Warm a few core routes once the page has loaded and gone idle. Kept short:
// import() executes each module and Vite applies its CSS to the current page.
function prefetchRoutes() {
  // Skip on auth routes — prefetching during login wastes bandwidth.
  if (AUTH_CONTROL_PATHS.has(globalThis.location?.pathname)) return;

  const prefetch = () => {
    import("../searchBarV2/SearchBarV2.js");
    import("../articlesV2/index.js");
    import("../dynamicPage/dynamicPage.component");
    import("../news/NewsDynamicPage.js");
  };

  const whenIdle = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(prefetch, { timeout: 10000 });
    } else {
      setTimeout(prefetch, 2000);
    }
  };

  if (document.readyState === "complete") {
    whenIdle();
  } else {
    globalThis.addEventListener("load", whenIdle, { once: true });
  }
}

// Set CSS variable for image base URL
document.documentElement.style.setProperty(
  "--static-bucket-url",
  AppSettings.AWSImgRepo.static,
);

// Bounded poll for Nitro to reflect freshly awarded points.
const NITRO_AWARD_POLL_ATTEMPTS = 4;
const NITRO_AWARD_POLL_INTERVAL = 1000;

function App({ router }) {
  const [hideContent, setHideContent] = useState(false);
  const [isNitroDataSet, setIsNitroDataSet] = useState(false);
  const [nitroLoadingState, setNitroLoadingState] = useState(false);
  const [visiblePopupTooltip, setVisiblePopupTooltip] = useState(null);
  const [notificationCount, setNotificationCount] = useState({
    unreadAnnouncements: 0,
  });
  const [nitroUserXPLevel, setNitroUserXPLevel] = useState();
  const [resizing, setResizing] = useState(false);
  const [windowWidth, setWindowWidth] = useState(globalThis.innerWidth);
  const [device, setDevice] = useState(null);
  const [userManager, setUserManager] = useState(null);
  const [isPrint, setIsPrint] = useState(false);
  const lastActivity = useUserActivity();
  const { personType, regionCode } = useCurrentUser();
  const queryClient = useQueryClient();

  const printableUtils = new printableDocumentEvents();

  // Created at boot (main.jsx), alongside the sign-in session.
  const initializeUserManager = () => {
    getUserManagerPromise()?.then(setUserManager, () => {});
  };

  useAuthTokenLifecycle(userManager, lastActivity);

  const loadNitro = (forceNitroReload = false) => {
    if (getCurrentUser().spinId && (forceNitroReload || !isNitroDataSet)) {
      getNitroUserData(forceNitroReload)
        .then((response) => {
          setIsNitroDataSet(true);
          setNitroUserXPLevel(response);
        })
        .catch((error) => {
          setNitroUserXPLevel(null);
          console.error("Failed to load nitro user data:", error);
        });
    }
  };

  // Nitro awards points asynchronously, so the balance right after an action is
  // often still the old one. Poll until it moves rather than guessing a delay.
  const refreshNitroXPAfterAward = async () => {
    if (!getCurrentUser().spinId) {
      return;
    }

    const previousPoints = nitroUserXPLevel?.nitroUserPoints;

    for (let attempt = 0; attempt < NITRO_AWARD_POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) =>
        setTimeout(resolve, NITRO_AWARD_POLL_INTERVAL),
      );

      try {
        const response = await getNitroUserData(true);
        setIsNitroDataSet(true);
        setNitroUserXPLevel(response);

        if (response?.nitroUserPoints !== previousPoints) {
          return;
        }
      } catch (error) {
        console.error("Failed to refresh nitro user data:", error);
        return;
      }
    }
  };

  const preventBackForwardCaching = () => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        globalThis.location.reload();
      }
    };

    globalThis.addEventListener("pageshow", handlePageShow);
    return () => globalThis.removeEventListener("pageshow", handlePageShow);
  };

  const setCurrentPopupTooltip = useCallback(
    (i) => {
      visiblePopupTooltip == i
        ? setVisiblePopupTooltip(null)
        : setVisiblePopupTooltip(i);
    },
    [visiblePopupTooltip],
  );

  // should run every minute as well as every time a notification becomes read in their respective components
  const getNotificationCount = useCallback(async () => {
    let notifications = {
      unreadAnnouncements: notificationCount.unreadAnnouncements,
    };

    await getAnnouncements().then((data) => {
      notifications.unreadAnnouncements = data.filter((i) => !i.isRead).length;
    });
    setNotificationCount(notifications);
  }, [notificationCount]);

  const getProfilePictureUrl = () => {
    if (!getCurrentUser().pictureUrl) {
      getProfile().then((userData) => {
        setCurrentUserPictureUrl(userData?.pictureUrl ?? "");
      });
    }
  };

  const setCorpUserCookie = () => {
    let engageCorpUserValue;

    switch (personType?.toLowerCase()) {
      case "associates":
      case "non-associates":
      case "affiliates":
        engageCorpUserValue = "tmna";
        break;
      case "private-distributors":
      case "dealer-user":
        if (regionCode === "600") {
          engageCorpUserValue = "gst";
        } else if (regionCode === "500") {
          engageCorpUserValue = "set";
        } else {
          engageCorpUserValue = "direct";
        }
        break;
      case "external":
        engageCorpUserValue = "direct";
        break;
      default:
        engageCorpUserValue = "unknown";
        break;
    }

    if (engageCorpUserValue != "unknown") {
      createCookie("engageCorpUser", engageCorpUserValue, 365, true);
    }
  };

  useEffect(() => {
    clearTimeout(globalThis.resizedFinished);
    setResizing(true);
    globalThis.resizedFinished = setTimeout(() => {
      setResizing(false);
    }, 250);
  }, [windowWidth]);

  const updateDevice = () => {
    setWindowWidth(globalThis.innerWidth);
    if (isDesktopView(globalThis.innerWidth)) {
      setDevice("desktop");
    } else if (isTabletView(globalThis.innerWidth)) {
      setDevice("tablet");
    } else if (isMobileView(globalThis.innerWidth)) {
      setDevice("mobile");
    }
  };

  const setPlatform = () => {
    const platform = globalThis.ReactNativeWebView ? "App" : "Web";
    createCookie("engagePlatform", platform, 365, true);
  };

  // Mount-only app shell setup; auth-gated data loading is handled separately below.
  useEffect(() => {
    const handleResize = () => {
      updateDevice();
    };

    setPlatform();
    initializeUserManager();
    const removeBackForwardCacheListener = preventBackForwardCaching();
    updateDevice();
    globalThis.addEventListener("resize", handleResize);

    // PRINTABLE
    printableUtils.subscribe();

    return () => {
      removeBackForwardCacheListener();
      globalThis.removeEventListener("resize", handleResize);
      printableUtils.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!userManager) {
      return;
    }

    let cancelled = false;

    // Signed-in only: an expired/missing session is about to redirect to login.
    // Page data is prefetched by the route loaders (see routePrefetch).
    userManager
      .getUser()
      .then((user) => {
        if (cancelled || !user || user.expired) {
          return;
        }
        prefetchRoutes();
      })
      .catch(() => {});

    // App-data phase is centralized so startup tasks run once even if multiple
    // auth events/effects trigger during initial hydration.
    const bootstrapAuthenticatedData = async () => {
      let sessionReady;
      try {
        sessionReady = await bootstrapAuthenticatedSession({
          phase: AUTH_BOOTSTRAP_PHASES.APP_DATA,
          userManager,
          queryClient,
          tasks: [() => getProfilePictureUrl(), () => getNotificationCount()],
        });
      } catch (error) {
        // Also rejects when the identity phase it waits on fails (e.g. an
        // expired account); that failure is handled by the flow that owns it.
        console.error("App data bootstrap failed.", error);
        return;
      }

      // No valid session yet: this boot is headed to sign-in, and the
      // user-loaded event re-runs this once one exists.
      if (cancelled || !sessionReady) {
        return;
      }

      // Nitro is not required for first paint; schedule during idle so the
      // authenticated shell/home can render with less startup pressure.
      const runDeferredStartupTasks = async () => {
        loadNitro();
      };

      if (typeof globalThis.requestIdleCallback === "function") {
        globalThis.requestIdleCallback(
          () => {
            if (cancelled) {
              return;
            }

            runDeferredStartupTasks();
          },
          { timeout: 2500 },
        );
      } else {
        setTimeout(() => {
          if (cancelled) {
            return;
          }

          runDeferredStartupTasks();
        }, 1200);
      }
    };

    const handleUserLoaded = () => {
      bootstrapAuthenticatedData();
    };

    bootstrapAuthenticatedData();
    userManager.events.addUserLoaded(handleUserLoaded);

    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(handleUserLoaded);
    };
  }, [userManager, queryClient]);

  useEffect(() => {
    setCorpUserCookie();
  }, [personType, regionCode]);

  const nitroContextValues = useMemo(
    () => ({
      setNitroUserXPLevel,
      nitroUserXPLevel,
      loadNitro,
      refreshNitroXPAfterAward,
      setNitroLoadingState,
      nitroLoadingState,
    }),
    [nitroUserXPLevel, nitroLoadingState],
  );

  const vehiclePagePrintValue = useMemo(
    () => ({ isPrint, setIsPrint }),
    [isPrint],
  );

  const windowSizeValue = useMemo(
    () => ({ resizing, device }),
    [resizing, device],
  );

  const userManagerContextValue = useMemo(
    () => ({ userManager, setUserManager }),
    [userManager],
  );

  const notificationContextValue = useMemo(
    () => ({
      setNotificationCount,
      notificationCount,
      getNotificationCount,
    }),
    [notificationCount, getNotificationCount],
  );

  const checkCurrentTooltipMatch = useCallback(
    (currentTooltip) => visiblePopupTooltip == currentTooltip,
    [visiblePopupTooltip],
  );

  const popupTooltipContextValue = useMemo(
    () => ({
      visiblePopupTooltip,
      setVisiblePopupTooltip,
      setCurrentPopupTooltip,
      checkCurrentTooltipMatch,
    }),
    [visiblePopupTooltip, setCurrentPopupTooltip, checkCurrentTooltipMatch],
  );

  const appShellValue = useMemo(
    () => ({
      hideContent,
      setHideContent,
      nitroLoadingState,
      nitroContextValues,
      vehiclePagePrintValue,
      windowSizeValue,
      userManagerContextValue,
      userManager,
      notificationContextValue,
      popupTooltipContextValue,
    }),
    [
      hideContent,
      nitroLoadingState,
      nitroContextValues,
      vehiclePagePrintValue,
      windowSizeValue,
      userManagerContextValue,
      userManager,
      notificationContextValue,
      popupTooltipContextValue,
    ],
  );

  return (
    <>
      {/* React 19 hoists <meta> into <head>. */}
      {isProdBuild ? null : <meta name="robots" content="noindex,nofollow" />}
      <AppShellContext.Provider value={appShellValue}>
        <RouterProvider router={router} />
      </AppShellContext.Provider>
    </>
  );
}

export default App;
